import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { JsonEditor } from '@library'
import { ActionsConfigTab } from '@mobileComponents/EventFlow/ActionsConfigTab'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { VariablesConfigTab } from '@mobileComponents/Variables'
import { DEFAULT_MOBILE_SEARCH_ASSISTANT } from '@mobileConstants'
import { validateMobileSearchAssistant } from '@mobileHelpers'
import { useMobileSearchAssistants } from '@mobileHooks'
import SimpleFiltersConfigTab from '@mobilePages/MobileSearchAssistantCreateOrEdit/components/SimpleFiltersConfigTab'
import { MobileSearchAssistantPreview } from '@mobilePreviews'
import { MobileSearchAssistant } from '@mobileTypes'
import { Box, Grid, Paper, Tab, Tabs, TextField } from '@mui/material'

import { ROUTES } from '@constants'

import { AdvancedFiltersConfigTab } from './components/AdvancedFiltersConfigTab'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props

  return (
    <div role='tabpanel' hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export const MobileSearchAssistantCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const { createSearchAssistant, updateSearchAssistant, getSearchAssistantById } =
    useMobileSearchAssistants()

  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')

  const methods = useForm<MobileSearchAssistant>({
    defaultValues: isEdit
      ? getSearchAssistantById(id) || DEFAULT_MOBILE_SEARCH_ASSISTANT
      : DEFAULT_MOBILE_SEARCH_ASSISTANT,
  })

  const { control, handleSubmit, reset, watch, formState } = methods

  const formData = watch()
  const { isDirty } = formState

  useEffect(() => {
    if (isEdit && id) {
      const searchAssistant = getSearchAssistantById(id)
      if (searchAssistant) {
        reset(searchAssistant)
        setJsonValue(JSON.stringify(searchAssistant, null, 2))
      }
    }
  }, [id, isEdit, getSearchAssistantById, reset])

  useEffect(() => {
    if (tabValue === 4) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [formData, tabValue])

  const [errors, setErrors] = useState<string[]>([])

  const onSubmit = (data: MobileSearchAssistant) => {
    const validationErrors = validateMobileSearchAssistant(data)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    if (isEdit && id) {
      updateSearchAssistant(id, data)
    } else {
      const newSearchAssistant = createSearchAssistant(data)
      // Navigate to edit mode after creation
      navigate(`${ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT}/${newSearchAssistant.id}`, {
        replace: true,
      })
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const searchAssistant = getSearchAssistantById(id)
      if (searchAssistant) {
        reset(searchAssistant)
        setJsonValue(JSON.stringify(searchAssistant, null, 2))
        setErrors([])
      }
    } else {
      reset(DEFAULT_MOBILE_SEARCH_ASSISTANT)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_SEARCH_ASSISTANT, null, 2))
      setErrors([])
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use setValue for each field to mark form as dirty
      Object.keys(parsed).forEach(key => {
        methods.setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch (error) {
      setJsonValue(newValue)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const previewComponent = useMemo(() => {
    if (tabValue === 1) {
      return <MobileSearchAssistantPreview searchAssistant={formData} />
    }

    return undefined
  }, [tabValue, formData])

  return (
    <MobileConfiguratorLayout
      title={isEdit ? 'Edit Mobile Search Assistant' : 'Create Mobile Search Assistant'}
      isEdit={isEdit}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={handleCancel}
      onReset={handleReset}
      isDirty={isDirty}
      errors={errors}
      previewComponent={previewComponent}
    >
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label='Basic Info' />
          <Tab label='Filters' />
          <Tab label='Variables' />
          <Tab label='Actions' />
          <Tab label='JSON Editor' />
        </Tabs>
      </Paper>

      <FormProvider {...methods}>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name='title'
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label='Title'
                    error={!!error}
                    helperText={error?.message || 'Display title for the search assistant'}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {formData.configMode === 'advanced' ? (
            <AdvancedFiltersConfigTab />
          ) : (
            <SimpleFiltersConfigTab />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <VariablesConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <ActionsConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <JsonEditor
            value={jsonValue}
            onChange={handleJsonChange}
            label='Search Assistant Configuration (JSON)'
            minHeight={400}
          />
        </TabPanel>
      </FormProvider>
    </MobileConfiguratorLayout>
  )
}
